import ReactMarkdown from 'react-markdown';

import { font } from 'atoms/typography/typography';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Text from 'quarks/Text';
import type { BasicProps } from 'quarks/interpolations/basic';

import Button, { ButtonProps } from 'molecules/Button/Button';

import BlogCard from 'components/Cards/BlogCard/BlogCard';
import ResourcesCard from 'components/Cards/ResourcesCard/ResourcesCard';

import type { IComponentCardDeckRecord, IComponentCardRecord } from 'graphql/types';
import type { FC } from 'react';

type TextStyles = keyof typeof font.display;

interface CardProps extends IComponentCardRecord {
  cardBody?: IComponentCardRecord['bodyCopy'];
}
interface CardDeckProps extends BasicProps, IComponentCardDeckRecord {
  backgroundIsDark: boolean;
  cardDeckBody?: IComponentCardDeckRecord['bodyCopy'];
  cards?: CardProps[];
  inContent?: boolean;
}

const ComponentCardDeck: FC<CardDeckProps> = ({
  backgroundIsDark,
  eyebrow,
  headline,
  headlineSizeDesktop,
  headlineSizeMobile,
  cardDeckBody,
  cards,
  button,
  inContent,
}) => (
  <Flex
    flexDirection="column"
    flexWrap="nowrap"
    textAlign="center"
    alignItems="center"
    marginX="auto"
    gap={inContent ? '0' : '48px'}
    textColor={backgroundIsDark ? 'common-white' : 'storm-900'}
  >
    <Flex flexDirection="column" gap="16px" maxWidth="880px" marginX="auto">
      {eyebrow && (
        <Text textTransform="uppercase" textStyle="sm" fontWeight="semiBold">
          {eyebrow}
        </Text>
      )}
      {headline && (
        <Heading
          as="h2"
          textStyle={(headlineSizeMobile as TextStyles) || 'sm'}
          md={{ textStyle: (headlineSizeDesktop as TextStyles) || 'md', fontWeight: 'bold' }}
          fontWeight="bold"
        >
          {headline}
        </Heading>
      )}
      {cardDeckBody && (
        <Container fontSize="textLg" md={{ fontSize: 'textXl' }} textColor={backgroundIsDark ? 'foam-50' : 'storm-600'}>
          <ReactMarkdown>{cardDeckBody || ''}</ReactMarkdown>
        </Container>
      )}
    </Flex>
    <Flex
      flexDirection="column"
      alignItems="center"
      flexWrap="wrap"
      width="100%"
      maxWidth="1170px"
      marginX="auto"
      gap="30px"
      justifyContent="center"
      lg={{ flexDirection: 'row', alignItems: 'unset' }}
    >
      {cards &&
        cards.map(card => {
          const CardComponent = card.variant === 'resources_card' ? ResourcesCard : BlogCard;

          return (
            <CardComponent
              noHover={false}
              isSlider={false}
              key={card.id}
              backgroundIsDark={backgroundIsDark}
              {...card}
            />
          );
        })}
    </Flex>
    {button && (
      <Button
        href={button?.link || ''}
        color={(button.variant as ButtonProps['color']) || 'primary'}
        size={(button.size as ButtonProps['size']) || 'md'}
        endIcon={button.endIcon}
      >
        {button.label}
      </Button>
    )}
  </Flex>
);

export default ComponentCardDeck;
